import {
  ArtworkHistory,
  useCreateArtworkHistory,
  useDeleteArtworkHistory,
  useEditArtworkHistory,
  useGetHistoryOfArtwork,
  useUpdateHistorySequence,
} from "@/api/artwork/artwork-history";
import { BooleanBadge } from "@/components/ui/boolean-badge";
import {
  AddButton,
  CancelButton,
  DeleteButton,
  SaveButton,
  UpdateButton,
} from "@/components/ui/custom-buttons";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "@/components/ui/custom-dialog";
import { DataTable } from "@/components/ui/datatable";
import { Dialog, DialogClose, DialogHeader } from "@/components/ui/dialog";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import * as AF from "@/components/form-items/AbstractForm";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
  oeuvresLocalisationHistoriqueHistoriqueNom: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueVille: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueDates: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere: z.boolean(),
});

type ArtworkHistoryForm = z.infer<typeof formSchema>;

type CreateOrEditArtworkHistoryProps = {
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  artworkHistory?: ArtworkHistory;
  artworkId: string;
};

export const CreateOrEditArtworkHistory = ({
  onOpenChange,
  open,
  artworkHistory,
  artworkId,
}: CreateOrEditArtworkHistoryProps) => {
  const isEdit = artworkHistory !== undefined;

  const updater = useEditArtworkHistory();
  const creator = useCreateArtworkHistory();
  const deleter = useDeleteArtworkHistory();

  const { t } = useTranslation();

  const form = useForm<ArtworkHistoryForm>({
    defaultValues: isEdit
      ? artworkHistory
      : {
          oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere: false,
          oeuvresLocalisationHistoriqueHistoriqueDates: "",
          oeuvresLocalisationHistoriqueHistoriqueNom: "",
          oeuvresLocalisationHistoriqueHistoriqueVille: "",
        },

    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (artworkHistory) {
      form.reset(artworkHistory);
    }
  }, [artworkHistory, form]);

  function onSubmit(values: ArtworkHistoryForm) {
    if (isEdit) {
      updater.mutate(
        {
          historyId: artworkHistory.id,
          artworkId: artworkHistory.artworkId,
          history: values,
        },
        { onSuccess: () => onOpenChange?.(false) },
      );
      return;
    }
    creator.mutate(
      { history: values, artworkId: artworkId },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange?.(false);
        },
      },
    );
  }
  function onDelete() {
    if (!isEdit) return;
    deleter.mutate(
      { artworkId: artworkHistory.artworkId, historyId: artworkHistory.id },
      {
        onSuccess: () => onOpenChange?.(false),
      },
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <CustomDialogContent>
        <Form {...form}>
          <form
            className="flex flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <DialogHeader>
              <CustomDialogTitle>{t("addArtworkHistory")}</CustomDialogTitle>
            </DialogHeader>
            <AF.Col className="p-6">
              <AF.Text name="oeuvresLocalisationHistoriqueHistoriqueNom" />
              <AF.Text name="oeuvresLocalisationHistoriqueHistoriqueVille" />
              <AF.Text name="oeuvresLocalisationHistoriqueHistoriqueDates" />
              <AF.Checkbox name="oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere" />
              {isEdit && (
                <DeleteButton
                  variant={"link"}
                  className="text-red-600 w-fit m-auto"
                  iconProps={{ className: "stroke-red-600" }}
                  onClick={onDelete}
                >
                  {t("supprimerOeuvresLocalisation")}
                </DeleteButton>
              )}
            </AF.Col>
            <CustomDialogFooter>
              <DialogClose asChild>
                <CancelButton className="flex-1" />
              </DialogClose>
              {isEdit ? (
                <UpdateButton type="submit" className="flex-1" />
              ) : (
                <SaveButton type="submit" className="flex-1" />
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

export const ArtworkHistoryTable = ({ artworkId }: { artworkId: string }) => {
  const { t } = useTranslation();

  const { data = [] } = useGetHistoryOfArtwork(artworkId);

  const [openEditForm, setOpenEditForm] = useState(false);
  const [openCreateForm, setOpenCreateForm] = useState(false);

  const historyToUpdate = useRef<ArtworkHistory>();

  const sequenceMutator = useUpdateHistorySequence();

  function handleUpdate(artworkHistory: ArtworkHistory) {
    historyToUpdate.current = artworkHistory;
    setOpenEditForm(true);
  }

  const datatableColumns = useMemo(
    (): ColumnDef<ArtworkHistory>[] => [
      {
        size: 1000,
        header: t("tab_oeuvresLocalisationHistoriqueHistoriqueNom"),
        accessorKey: "oeuvresLocalisationHistoriqueHistoriqueNom",
      },
      {
        header: t("tab_oeuvresLocalisationHistoriqueHistoriqueVille"),
        accessorKey: "oeuvresLocalisationHistoriqueHistoriqueVille",
      },
      {
        header: t("tab_oeuvresLocalisationHistoriqueHistoriqueDates"),
        accessorKey: "oeuvresLocalisationHistoriqueHistoriqueDates",
      },
      {
        header: t(
          "tab_oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
        ),
        cell: (v) => (
          <BooleanBadge
            value={
              v.row.original
                .oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere
            }
          />
        ),
      },
    ],
    [t],
  );

  const handleRowDragged = async (newData: ArtworkHistory[]) => {
    const newSequence = newData.map((d) => d.id);
    await sequenceMutator.mutate({
      newSequence,
      artworkId,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <CreateOrEditArtworkHistory
        artworkId={artworkId}
        artworkHistory={historyToUpdate.current}
        open={openEditForm}
        onOpenChange={setOpenEditForm}
      />
      <CreateOrEditArtworkHistory
        open={openCreateForm}
        onOpenChange={setOpenCreateForm}
        artworkId={artworkId}
      />
      <div className=" flex justify-between items-baseline">
        <label className="text-sm font-medium text-gray-700">
          {t("oeuvresLocalisationHistorique")}
        </label>
        <AddButton type="button" onClick={() => setOpenCreateForm(true)} />
      </div>
      <DataTable
        enableDragRows
        onRowDragged={(d) => handleRowDragged(d.newData)}
        columns={datatableColumns}
        data={data}
        onRowClick={(e) => handleUpdate(e.original)}
      />
    </div>
  );
};
